import { startRegistration } from '@simplewebauthn/browser';
import { useState } from 'react';

import { PageHeader } from '@/components/layout/PageHeader';
import { RecoveryCodeList, TotpSetupFlow } from '@/components/settings/TotpSetupFlow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, Td, Th } from '@/components/ui/table';
import { ApiError, api, type LoginSession, type Me, type PasskeyCredential } from '@/lib/api';
import { useApi } from '@/lib/useApi';

/**
 * 自分のアカウント。
 *
 * API は Phase 2 から揃っていたのに画面が無く、TOTP も Passkey も
 * 有効化できなかった。ここが本人だけが触れる唯一の入口。
 */
export function SecurityPage({ me, onChanged }: { me: Me; onChanged: () => void }) {
  return (
    <>
      <PageHeader title="アカウント" subtitle={me.user.email} />

      <div className="grid gap-8 p-4 md:p-8 lg:grid-cols-2">
        <ProfileSection me={me} onChanged={onChanged} />
        <PasswordSection />
        <TotpSection me={me} onChanged={onChanged} />
        <PasskeySection />
        <SessionSection onSignedOut={onChanged} />
      </div>
    </>
  );
}

/**
 * ログイン中の端末。
 *
 * 端末を紛失したときに、パスワードを変えずに 1 台だけ落とせるようにする。
 * 並んでいる id はトークンの SHA-256 なので、見えてもセッションは復元できない。
 */
function SessionSection({ onSignedOut }: { onSignedOut: () => void }) {
  const { data, error, loading, reload } = useApi<{ sessions: LoginSession[] }>(
    '/api/auth/sessions',
  );
  const [busy, setBusy] = useState(false);

  return (
    <Section title="ログイン中の端末">
      <p className="text-xs leading-relaxed text-ink-subtle">
        見覚えのない端末があれば切断してください。パスワードを変更すると、
        操作中の端末以外はまとめて切れます。
      </p>

      {loading && data === null ? (
        <span className="text-xs text-ink-subtle">読み込み中…</span>
      ) : null}
      {error ? <span className="text-sm text-alert">{error.message}</span> : null}

      {data ? (
        <div className="border border-line">
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>LAST USED</Th>
                  <Th>CLIENT</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((session) => (
                  <tr key={session.id}>
                    <Td className="font-mono text-xs text-ink-secondary">
                      {new Date(session.lastUsedAt * 1000).toLocaleString('ja-JP')}
                    </Td>
                    <Td className="max-w-64 truncate text-xs text-ink-subtle">
                      {session.current ? (
                        <Badge variant="outline">この端末</Badge>
                      ) : (
                        (session.userAgent ?? '不明')
                      )}
                    </Td>
                    <Td className="text-right">
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await api.delete(`/api/auth/sessions/${session.id}`);
                            // 自分自身を切ったなら未ログインに戻る
                            if (session.current) onSignedOut();
                            else reload();
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        {session.current ? 'ログアウト' : '切断'}
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </div>
      ) : null}
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    /*
     * `min-w-0` はモバイルのため。grid の子は既定で `min-width: auto` になり、
     * 中の表の最小幅がそのまま列幅を押し広げる (端末一覧は User-Agent の
     * セルが 256px を要求する)。0 にしておくと `TableWrap` の
     * `overflow-x-auto` が本来の仕事をして、表だけが器の中でスクロールする。
     * 幅に余裕がある 768px 以上では最小幅が効かないので描画は変わらない。
     */
    <section className="flex min-w-0 flex-col gap-4 border border-line bg-surface-raised px-4 py-5 md:px-6">
      <h2 className="text-sm font-bold">{title}</h2>
      {children}
    </section>
  );
}

function ProfileSection({ me, onChanged }: { me: Me; onChanged: () => void }) {
  const [name, setName] = useState(me.user.name);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  return (
    <Section title="プロフィール">
      <Field label="表示名">
        <Input value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
      </Field>
      <div className="flex items-center gap-4">
        <Button
          variant="primary"
          disabled={busy || name.trim() === ''}
          onClick={async () => {
            setBusy(true);
            setMessage(null);
            try {
              await api.patch('/api/auth/profile', { name });
              setMessage('保存しました');
              onChanged();
            } catch (cause) {
              setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
            } finally {
              setBusy(false);
            }
          }}
        >
          保存
        </Button>
        {message ? <span className="text-xs text-ink-muted">{message}</span> : null}
      </div>
    </Section>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  return (
    <Section title="パスワード">
      <Field label="現在のパスワード">
        <Input
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="新しいパスワード" hint="12 文字以上。">
        <Input
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          disabled={busy}
        />
      </Field>
      <div className="flex items-center gap-4">
        <Button
          variant="primary"
          disabled={busy || current === '' || next.length < 12}
          onClick={async () => {
            setBusy(true);
            setMessage(null);
            try {
              await api.post('/api/auth/password', {
                currentPassword: current,
                newPassword: next,
              });
              setCurrent('');
              setNext('');
              setMessage('変更しました。他の端末のセッションは切断されました。');
            } catch (cause) {
              setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
            } finally {
              setBusy(false);
            }
          }}
        >
          変更
        </Button>
        {message ? <span className="text-xs text-ink-muted">{message}</span> : null}
      </div>
    </Section>
  );
}

function TotpSection({ me, onChanged }: { me: Me; onChanged: () => void }) {
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      onChanged();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  if (me.totpEnabled) {
    return (
      <Section title="二段階認証 (TOTP)">
        <div className="flex items-center gap-2">
          <Badge>有効</Badge>
          <span className="text-xs text-ink-muted">
            未使用のリカバリーコード {me.unusedRecoveryCodes} 枚
          </span>
        </div>

        {recoveryCodes ? <RecoveryCodeList codes={recoveryCodes} /> : null}

        <Field label="パスワード" hint="リカバリーコードの再発行と解除に必要です。">
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={busy || password === ''}
            onClick={() =>
              run(async () => {
                const result = await api.post<{ recoveryCodes: string[] }>(
                  '/api/auth/totp/recovery-codes',
                  { password },
                );
                setRecoveryCodes(result.recoveryCodes);
                setPassword('');
              })
            }
          >
            リカバリーコードを再発行
          </Button>
          <Button
            variant="ghost"
            disabled={busy || password === ''}
            onClick={() =>
              run(async () => {
                await api.post('/api/auth/totp/disable', { password });
                setPassword('');
                setRecoveryCodes(null);
              })
            }
          >
            二段階認証を解除
          </Button>
        </div>
        {message ? <p className="text-sm text-alert">{message}</p> : null}
      </Section>
    );
  }

  return (
    <Section title="二段階認証 (TOTP)">
      {/* 登録フローはテナントの必須化で出す画面と共有する */}
      {recoveryCodes ? <RecoveryCodeList codes={recoveryCodes} /> : null}
      <TotpSetupFlow
        onEnabled={(codes) => {
          // 有効化すると me が入れ替わって「有効」の表示に差し替わるので、
          // 一度きりの平文はこちら側で持つ (下の分岐でも同じ state を出す)
          setRecoveryCodes(codes);
          onChanged();
        }}
      />
    </Section>
  );
}

function PasskeySection() {
  const { data, error, loading, reload } = useApi<{ credentials: PasskeyCredential[] }>(
    '/api/auth/passkey/credentials',
  );
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function register() {
    setBusy(true);
    setMessage(null);
    try {
      const options = await api.post<{ challengeId: string; options: unknown }>(
        '/api/auth/passkey/registration/options',
      );
      const response = await startRegistration({
        optionsJSON: options.options as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      await api.post('/api/auth/passkey/registration/verify', {
        challengeId: options.challengeId,
        label: label.trim() || undefined,
        response,
      });
      setLabel('');
      reload();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '登録をキャンセルしました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Passkey">
      <p className="text-xs leading-relaxed text-ink-subtle">
        端末の生体認証や PIN でログインできるようになります。
      </p>

      {loading ? <span className="text-xs text-ink-subtle">読み込み中…</span> : null}
      {error ? <span className="text-sm text-alert">{error.message}</span> : null}

      {data && data.credentials.length > 0 ? (
        <div className="border border-line">
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>DEVICE</Th>
                  <Th>REGISTERED</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.credentials.map((credential) => (
                  <tr key={credential.id}>
                    <Td>{credential.label ?? '名前なし'}</Td>
                    <Td className="font-mono text-xs text-ink-subtle">
                      {new Date(credential.createdAt * 1000).toLocaleDateString('ja-JP')}
                    </Td>
                    <Td className="text-right">
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await api.delete(`/api/auth/passkey/credentials/${credential.id}`);
                            reload();
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        削除
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </div>
      ) : null}

      <FieldRow className="gap-x-2">
        <Field label="端末名" className="flex-1">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="例: 業務用 MacBook"
            disabled={busy}
          />
        </Field>
        <Button variant="primary" onClick={register} disabled={busy}>
          この端末を登録
        </Button>
      </FieldRow>
      {message ? <p className="text-sm text-alert">{message}</p> : null}
    </Section>
  );
}
